/**
 * 拆分图组消失排查·画布操作时间线（2026-09-02，TB-9293 后续）
 *
 * 背景：画廊「定位/复制到画布」后出现已拆分图组整体消失。静态审计确认复制/定位
 * 链路本身无删除代码，删除只能发生在历史恢复（smartRestoreCanvas 按 id 差量移除）、
 * 手动删除、resetBoard 等路径——但没有运行时证据无法定位。本模块提供**跨刷新持久**
 * 的运行时时间线：
 *
 * 1. installSplitCanvasTimeline 监听画布 object:added（仅拆分图组）与 object:removed
 *    （全部对象）——任何删除路径（undo/redo 历史恢复、手动删除、同步逻辑、resetBoard）
 *    都必须经过 object:removed 事件，无一遗漏；
 * 2. 历史恢复（smartRestoreCanvas）、复制/定位链路、拆分落地、画板重置、保存链路显式打点；
 * 3. 环形缓冲（500 条）防抖写入 localStorage（key: pinoSplitDiagTimeline），
 *    刷新/重载后现场不丢——「消失后再刷新才能看到」的场景也能取证；
 * 4. Console 全局接口 window.__splitDiag：
 *    - tail(n?)        最近 n 条时间线（默认 50）
 *    - dump()          全量时间线打印 + 返回 JSON 字符串（整段复制给排查）
 *    - snap()          当前画布拆分图组清单（taskId/clientId/子对象数），打印并返回
 *    - clear()         清空时间线
 */

import { fabric } from "fabric";
import {
  SPLIT_GROUP_TAG,
  SPLIT_TASK_ID_TAG,
  SPLIT_CLIENT_ID_TAG,
  SPLIT_SOURCE_CLIENT_ID_TAG,
  SPLIT_SOURCE_VERSION_ID_TAG,
} from "../components/ImageContextToolbar/GameUiSplitterPanel/constants";
import { useEditorStore } from "./store/editor";
import { useBoardHistoryStore } from "./store/history";

export interface SplitTimelineEntry {
  /** 浏览器时间戳（ms） */
  ts: number;
  /** 事件类型：object-removed / group-added / restore-begin / restore-removed / restore-done / copy-* / locate-* / board-reset / save-* 等 */
  ev: string;
  /** 事件上下文（plain object，JSON-safe） */
  data?: Record<string, unknown>;
}

const STORAGE_KEY = "pinoSplitDiagTimeline";
const MAX_ENTRIES = 500;
const PERSIST_DEBOUNCE_MS = 400;

let timeline: SplitTimelineEntry[] = [];
let persistTimer: ReturnType<typeof setTimeout> | null = null;

function loadFromStorage(): SplitTimelineEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistSoon() {
  if (typeof window === "undefined" || !window.localStorage) return;
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(timeline));
    } catch {
      // 配额满等异常静默：时间线是诊断辅助，不应影响业务
    }
  }, PERSIST_DEBOUNCE_MS);
}

function ensureLoaded() {
  if (!timeline.length) timeline = loadFromStorage();
}

/** 记录一条时间线。opts.silent: 只入缓存不打印 console（高频/噪音事件用） */
export function splitTimeline(
  ev: string,
  data?: Record<string, unknown>,
  opts?: { silent?: boolean },
): void {
  try {
    ensureLoaded();
    timeline.push({ ts: Date.now(), ev, data });
    while (timeline.length > MAX_ENTRIES) timeline.shift();
    persistSoon();
    if (!opts?.silent) {
      // eslint-disable-next-line no-console
      console.log(`[SplitDiag] ${ev}`, data ?? "");
    }
  } catch {
    // 诊断记录绝不抛错影响业务
  }
}

/** 拆分图组身份摘要（JSON-safe） */
export function describeSplitObj(obj: any): Record<string, unknown> {
  return {
    type: obj?.type ?? "?",
    id: obj?.id ?? "",
    name: obj?.name ?? "",
    taskId: obj?.[SPLIT_TASK_ID_TAG] ?? "",
    clientId: obj?.[SPLIT_CLIENT_ID_TAG] ?? "",
    sourceClientId: obj?.[SPLIT_SOURCE_CLIENT_ID_TAG] ?? "",
    sourceVersionId: obj?.[SPLIT_SOURCE_VERSION_ID_TAG] ?? "",
    children: typeof obj?.getObjects === "function" ? obj.getObjects().length : 0,
  };
}

/** 任意画布对象身份摘要（object:removed 全量记录用） */
export function describeAnyObj(obj: any): Record<string, unknown> {
  const isSplitGroup = Boolean(obj?.[SPLIT_GROUP_TAG]);
  const desc: Record<string, unknown> = {
    type: obj?.type ?? "?",
    id: obj?.id ?? "",
    name: obj?.name ?? "",
    isSplitGroup,
  };
  if (isSplitGroup) {
    desc.taskId = obj?.[SPLIT_TASK_ID_TAG] ?? "";
    desc.clientId = obj?.[SPLIT_CLIENT_ID_TAG] ?? "";
    desc.children = typeof obj?.getObjects === "function" ? obj.getObjects().length : 0;
  }
  return desc;
}

/** 当前画布拆分图组清单（快照式盘点） */
export function describeSplitGroups(canvas: any): Record<string, unknown> {
  const objs: any[] = canvas?.getObjects?.() ?? [];
  const groups = objs.filter((o) => o?.[SPLIT_GROUP_TAG]);
  return {
    totalObjects: objs.length,
    splitGroupCount: groups.length,
    splitGroups: groups.map(describeSplitObj),
  };
}

/** 序列化画布数据（layerData.objects / 快照 objects）中拆分图组计数（保存链路打点用） */
export function countSerializedSplitGroups(objects: unknown): number {
  return Array.isArray(objects) ? objects.filter((o: any) => o?._isSplitGroup).length : 0;
}

/**
 * 序列化画布数据中每个拆分图组的子对象数（保存链路打点用）。
 * children 归零=空组不可见（「图组消失」主形态），保存明细能定位 children 丢失发生在哪次保存前后。
 */
export function describeSerializedSplitGroups(objects: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(objects)) return [];
  return objects
    .filter((o: any) => o?._isSplitGroup)
    .map((o: any) => ({
      taskId: o?._splitTaskId ?? "",
      children: Array.isArray(o?.objects) ? o.objects.length : 0,
    }));
}

/** 精简调用栈（首 6 个业务帧，单行）——定位「谁删的」 */
export function briefStack(): string {
  try {
    return String(new Error().stack || "")
      .split("\n")
      .slice(2, 9)
      .map((l) => l.trim().replace(/^at\s+/, ""))
      .join(" <- ");
  } catch {
    return "";
  }
}

/**
 * Group 子对象增删补丁：任何 group.remove(child) / group.add(child)（拆分图组）都记录。
 * 子对象从组内移除不触发画布 object:removed（canvas 级监听抓不到），只能在此捕获；
 * stack 直指调用方（详情同步 / 修补替换 / 删除图层 / 自愈差量）。
 */
function patchGroupChildOps(): void {
  const proto: any = (fabric as any)?.Group?.prototype;
  if (!proto || proto.__splitChildOpsPatched) return;
  proto.__splitChildOpsPatched = true;

  const origRemove = proto.remove;
  proto.remove = function (...args: any[]) {
    try {
      if (this?.[SPLIT_GROUP_TAG] && args.length) {
        splitTimeline("group-child-removed", {
          taskId: this[SPLIT_TASK_ID_TAG] || "",
          removed: args.map(describeAnyObj),
          remaining: (this.getObjects?.() || []).length - args.length,
          stack: briefStack(),
        });
      }
    } catch {
      // ignore
    }
    return origRemove.apply(this, args);
  };

  const origAdd = proto.add;
  proto.add = function (...args: any[]) {
    try {
      if (this?.[SPLIT_GROUP_TAG] && args.length) {
        splitTimeline(
          "group-child-added",
          {
            taskId: this[SPLIT_TASK_ID_TAG] || "",
            added: args.map(describeAnyObj),
            total: (this.getObjects?.() || []).length + args.length,
            stack: briefStack(),
          },
          { silent: true },
        );
      }
    } catch {
      // ignore
    }
    return origAdd.apply(this, args);
  };
}

function safeCanvas(): any {
  try {
    return useEditorStore().fabricCanvas as any;
  } catch {
    return null;
  }
}

function safeRestoring(): boolean {
  try {
    return useBoardHistoryStore().isRestoringHistory;
  } catch {
    return false;
  }
}

/**
 * 给画布安装时间线监听（幂等；每块新画布实例装一次）。
 * - object:removed 全量记录（携带 isRestoringHistory 标记，区分「历史恢复删除」与「常规删除」）
 * - object:added 仅记录拆分图组（普通对象由各业务链路打点，避免噪音）
 */
export function installSplitCanvasTimeline(canvas: any): void {
  if (!canvas || (canvas as any).__splitTimelineInstalled) return;
  (canvas as any).__splitTimelineInstalled = true;

  patchGroupChildOps();

  canvas.on("object:added", (e: any) => {
    try {
      const obj = e?.target;
      if (!obj || !obj[SPLIT_GROUP_TAG]) return;
      splitTimeline("group-added", { group: describeSplitObj(obj), restoring: safeRestoring() });
    } catch {
      // ignore
    }
  });

  canvas.on("object:removed", (e: any) => {
    try {
      const obj = e?.target;
      if (!obj) return;
      const isGroupLike = obj.type === "group" || obj[SPLIT_GROUP_TAG];
      splitTimeline(
        "object-removed",
        {
          obj: describeAnyObj(obj),
          restoring: safeRestoring(),
          // group 移除低频但关键（整组删除路径定责），附带精简调用栈
          ...(isGroupLike ? { stack: briefStack() } : {}),
        },
      );
    } catch {
      // ignore
    }
  });

  splitTimeline("timeline-installed", {}, { silent: true });
}

/** 记录当前画布拆分图组盘点（复制/定位等关键动作前后调用，reason 标记动作） */
export function snapshotInventory(reason: string, extra?: Record<string, unknown>): void {
  splitTimeline("inventory", { reason, ...(extra ?? {}), canvas: describeSplitGroups(safeCanvas()) });
}

export interface SplitDiagGlobal {
  tail: (n?: number) => SplitTimelineEntry[];
  dump: () => string;
  snap: () => string;
  clear: () => void;
}

declare global {
  interface Window {
    __splitDiag?: SplitDiagGlobal;
  }
}

function ensureGlobal(): void {
  if (typeof window === "undefined" || window.__splitDiag) return;
  window.__splitDiag = {
    tail: (n = 50) => {
      ensureLoaded();
      return timeline.slice(-n);
    },
    dump: () => {
      ensureLoaded();
      const json = JSON.stringify(timeline, null, 2);
      // eslint-disable-next-line no-console
      console.log(
        `[SplitDiag] 时间线共 ${timeline.length} 条（localStorage[${STORAGE_KEY}]，刷新不丢）。以下为全量 JSON，可整段复制：`,
      );
      // eslint-disable-next-line no-console
      console.log(json);
      return json;
    },
    snap: () => {
      const inv = describeSplitGroups(safeCanvas());
      const json = JSON.stringify(inv, null, 2);
      // eslint-disable-next-line no-console
      console.log("[SplitDiag] 当前画布拆分图组清单:", inv);
      return json;
    },
    clear: () => {
      timeline = [];
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
      // eslint-disable-next-line no-console
      console.log("[SplitDiag] 时间线已清空");
    },
  };
}

ensureGlobal();
